/* dashboard/sse.js — SSE connection + incoming-data fanout.
 *
 * Extracted from static/js/live.js on 2026-05-31 (Phase L5 of the
 * live.js refactor, see memory [[user-dashboards-architecture]]).
 *
 * Same mixin-style pattern as LSEventDetection / LSMediaPlayback —
 * methods rely on `this` being bound to the dashboard controller,
 * so live.js delegates with .call(this, ...). The EventSource
 * instance itself lives on the controller as this._sse so the rest
 * of the codebase (selectSlate teardown, etc.) can still see it.
 *
 * This module is the SINGLE ingest path for live state on the
 * dashboard. applyData runs on every SSE tick and is responsible
 * for:
 *   - dispatching match/inning events (this.detectGameEvents)
 *   - score-bump animation flags (this.scoreBumpIds)
 *   - merging incoming games/players into controller arrays
 *   - auto-unpinning finished games (preserving user pins)
 *   - dispatching stat-delta events (this.detectEvents,
 *     this._processMatchEvents, this._detectPersonalMoments)
 *   - merging PbP feed (MLB broadcast tier)
 *   - per-player score flash + delta overlay timing
 *   - writing prevStats / prevAllStats snapshots for next-tick diffs
 *   - merging lineup / dream-team / salary cap
 *   - stamping last_updated / data_updated_at and flipping dataReady
 *
 * Public API (all called with controller as `this`):
 *   sseUrl()       → string | null
 *   connectSSE()   → (re)opens this._sse, wires onmessage/onerror
 *                     with 5s back-off on persistent failure, and a
 *                     wake/stale watchdog (_installWake) for phones
 *   applyData(data)→ the giant fanout described above
 *
 * Controller state read/written is too broad to enumerate cleanly
 * here — see the inline comments. Roughly: anything reactive on the
 * dashboard either originates in applyData or is downstream of it.
 */

window.LSSse = {

  sseUrl() {
    if (!this.selectedSlate) return null;
    // Showcase demo — the auth-free looping stream (routes/demo.py). The demo
    // payload is the same shape, so everything downstream renders unchanged.
    if (this.demoShowcase && window.DEMO) {
      return `/api/sse/demo-live/${encodeURIComponent(window.DEMO.slug)}`;
    }
    const tid   = this.selectedSlate.template?.id || "";
    const csid  = this.selectedSlate.id || "";
    const plat  = this.selectedSlate.platform_id || this.selectedSlate.platform?.id || "fanteam";
    const sport = this.selectedSlate.sport_id    || this.selectedSlate.sport?.id    || "nba";
    return `/api/sse/slate-live?slate_template_id=${encodeURIComponent(tid)}&slate_id=${encodeURIComponent(csid)}&platform_id=${encodeURIComponent(plat)}&sport_id=${encodeURIComponent(sport)}&tab=${LSSse.TAB_ID}`;
  },

  // One id per page load. The server keys streams by (user, tab), so this tab
  // re-connecting replaces only its own stream, and the same account open on a
  // desktop and a phone keeps both alive (it used to be one stream per user —
  // they took turns knocking each other off). Per page load rather than
  // sessionStorage because "Duplicate tab" copies sessionStorage, and two tabs
  // sharing an id would be back to killing each other.
  TAB_ID: Math.random().toString(36).slice(2, 12),

  // A stream that has said nothing for this long is presumed dead. The server
  // sends a 'ping' event after 55s of quiet, so 90s leaves room for a slow
  // build or a jittery network without false alarms.
  STALE_MS: 90000,

  /* Phones are where a stream dies quietly: lock the screen, the OS freezes the
   * tab, and on wake the EventSource can sit half-open for minutes looking
   * connected while nothing arrives. So: reconnect on becoming visible or coming
   * back online if the stream is closed or stale, plus a watchdog while visible.
   * Installed once per controller; a no-op while no stream is open (demo mode,
   * or a 5s retry already pending). */
  /** Fold a new frame into the one we are already holding for a hidden tab.
   *
   *  Keeping only the NEWEST frame loses events, and it is not obvious why:
   *  each push after the first carries only the events that stream has not
   *  sent yet (core/slate_live.lean_events_delta), so an event in an earlier
   *  held frame is already marked as sent and never arrives again. Lock,
   *  glance, lock - a phone's normal rhythm - and the wire skipped your home
   *  run, then replayed it minutes later at the ten-minute full re-sync, with
   *  sound, as if it had just happened (audit LIVE-1, 2026-09-16).
   *
   *  State (scores, totals, players) is a snapshot, so the newest wins. Event
   *  LISTS are deltas, so they accumulate, deduped by id, oldest first. */
  _mergeHeld(held, next) {
    if (!held) return next;
    const out = Object.assign({}, held, next);      // newest state wins
    for (const key of ['scorer_events', 'match_events', 'pbp_events']) {
      const a = held[key] || [], b = next[key] || [];
      if (!a.length) { if (b.length) out[key] = b; continue; }
      const seen = new Set();
      const merged = [];
      for (const ev of a.concat(b)) {
        const id = ev && (ev.id != null ? ev.id
                   : ev.event_id != null ? ev.event_id
                   : ev.at_bat_index != null ? ev.at_bat_index
                   : JSON.stringify(ev));
        const k = String(id);
        if (seen.has(k)) continue;
        seen.add(k);
        merged.push(ev);
      }
      out[key] = merged;
    }
    return out;
  },

  _installWake() {
    if (this._sseWakeInstalled) return;
    this._sseWakeInstalled = true;
    const check = (ev) => {
      if (document.visibilityState !== 'visible') return;
      // A retired stream stays retired. Without this the watchdog would
      // resurrect it on the next tab focus — the slate is over, so it would
      // reconnect, be retired again, and we would have built a loop out of the
      // fix for one. connectSSE() clears the flag when a NEW slate is opened.
      if (this._sseEnded) return;
      // Catch up with the one frame we held while the screen was off.
      if (this._ssePending) {
        const held = this._ssePending;
        this._ssePending = null;
        this._catchUpApply = true;          // silent: see event_detection's batch push
        try { this._applyData(held); } catch (_) {}
        this._catchUpApply = false;
      }
      if (!this._sse) {
        // Backing off with no stream open, and the network (or the screen) has
        // just come back: the wait is over. After five failures the back-off
        // is up to 90s, and a phone that was plainly online again sat it out
        // (run 8, LIVE-15). Only for a real `online` / visible event - the 15s
        // interval calling this would undo the back-off's whole point.
        if (ev && ev.type !== 'offline' && this._sseRetry && navigator.onLine !== false) {
          this.sseReconnecting = true;
          this._connectSSE();
        }
        return;
      }
      const dead  = this._sse.readyState === 2;   // EventSource.CLOSED
      const stale = Date.now() - (this._sseLastMsg || 0) > LSSse.STALE_MS;
      if (dead || stale) {
        this.sseReconnecting = true;
        this._connectSSE();
      }
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', check);
    // ...and `offline`. Listening only for `online` meant losing the network
    // changed nothing until a poll happened to fail, so the header kept its
    // last verdict - including a green LIVE - across the whole outage.
    window.addEventListener('offline', check);
    setInterval(check, 15000);
  },

  connectSSE() {
    // Cancel any pending reconnect timer so a slate-switch (or manual reconnect)
    // during the 5s backoff can't leave an orphaned timer that fires later and
    // opens a SECOND EventSource — which would double-process every tick.
    if (this._sseRetry) { clearTimeout(this._sseRetry); this._sseRetry = null; }
    if (this._sse) { this._sse.close(); this._sse = null; }
    // A DELIBERATE connect clears the retirement: switching to a live slate
    // after watching a finished one must stream again. Only the server's
    // `ended` event sets it, and only this path clears it, so a retired stream
    // can never revive itself — but the user can always move on.
    this._sseEnded = false;
    // Showcase demo — no server stream. A client-driven clock polls the
    // snapshot endpoint so the user can play / pause / restart / change speed.
    if (this.demoShowcase && window.DEMO) { this._demoStart(); return; }
    const url = this._sseUrl();
    if (!url) return;

    this._sse = new EventSource(url);
    this._sseLastMsg = Date.now();
    LSSse._installWake.call(this);

    this._sse.onopen = () => { this.sseReconnecting = false; this._sseFails = 0; this._sseLastMsg = Date.now(); };

    this._sse.onmessage = (e) => {
      // The first push after a reconnect carries whatever was missed while
      // the stream was down - caught up silently, like a held frame.
      const afterReconnect = !!this.sseReconnecting;
      this.sseReconnecting = false;   // stream is alive again
      this._sseLastMsg = Date.now();
      let payload;
      try { payload = JSON.parse(e.data); } catch (_) { return; }
      // In a pocket, the stream kept arriving and every frame re-rendered the
      // whole board - full layout work, every 30s, for a screen nobody is
      // looking at, on a battery (audit re-run 2026-09-12). Hold the NEWEST
      // frame instead and apply exactly one when the screen comes back, which
      // is also what makes the wire show what you missed rather than replaying
      // it. The connection itself stays open - dropping it would mean a
      // reconnect storm every time someone glances at a notification.
      if (document.visibilityState === 'hidden') {
        this._ssePending = LSSse._mergeHeld(this._ssePending, payload);
        return;
      }
      this._catchUpApply = afterReconnect;
      try { this._applyData(payload); } finally { this._catchUpApply = false; }
    };

    // Server keepalive — proves a quiet stream is still alive (see _installWake).
    this._sse.addEventListener('ping', () => { this._sseLastMsg = Date.now(); });
    // At capacity the server refuses with a back-off hint, then closes. Keep the
    // hint for onerror below, which is what actually schedules the reconnect.
    this._sse.addEventListener('busy', (e) => {
      try { this._sseRetryHint = +JSON.parse(e.data).retry || 0; } catch (_) { this._sseRetryHint = 10000; }
    });

    // The server has retired this stream: the slate's games are over, the
    // poller has let it go, and no push will ever carry anything new again.
    //
    // This exists because NOTHING used to end a stream. The server loop exits
    // on disconnect, kill or shutdown, and a tab left open is none of those —
    // one ran for three days on a slate that finished on the Sunday (found
    // 2026-10-14), holding an SSE slot, rebuilding payloads and keeping the
    // owner's session last_seen warm enough that the Back Office called it a
    // fresh login.
    //
    // It has to be a CLEAN, FLAGGED close. EventSource reconnects on any
    // server-side close, so without this the retirement would start a loop
    // instead of ending one — the same trap the 'busy' event was added for.
    // Closing here means onerror either never fires or returns immediately.
    this._sse.addEventListener('ended', () => {
      this._sseEnded = true;
      if (this._sseRetry) { clearTimeout(this._sseRetry); this._sseRetry = null; }
      if (this._sse) { this._sse.close(); this._sse = null; }
      // NOT "Reconnecting…": nothing is coming back, and a spinner that never
      // resolves is worse than a board that simply stopped. The numbers on
      // screen are the final ones — we closed a pipe, we did not clear a page.
      this.sseReconnecting = false;
    });

    this._sse.onerror = () => {
      // Retired streams close themselves above; a close we asked for is not a
      // failure and must not schedule a reconnect.
      if (this._sseEnded) return;
      // EventSource auto-reconnects, but on persistent failure back off 5s. Flag
      // it so the freshness pill shows "Reconnecting…" immediately, rather than
      // the user watching scores freeze until the 60s staleness threshold.
      this.sseReconnecting = true;
      if (this._sse) { this._sse.close(); this._sse = null; }
      if (this._sseRetry) clearTimeout(this._sseRetry);
      // BACK OFF, AND JITTER. A flat 5s meant every phone watching a slate
      // reconnected in the same second after a deploy - N clients in one
      // moment, each costing a full payload build - and a server refusing
      // at capacity (retry: 10000) was asked again in five (audit LIVE-8).
      this._sseFails = (this._sseFails || 0) + 1;
      const _base  = Math.min(5000 * Math.pow(2, this._sseFails - 1), 60000);
      // x0.25-1.75, not x0.5-1.5: a hundred phones dropped by one deploy spread
      // their first reconnects over ~4.8s at the narrower band, under the 5s the
      // audit asks for (run 8, LIVE-8). And never sooner than the server said.
      const _floor = this._sseRetryHint || 0;
      this._sseRetryHint = 0;
      const _delay = Math.max(_floor, Math.round(_base * (0.25 + Math.random() * 1.5)));
      this._sseRetry = setTimeout(() => this._connectSSE(), _delay);
    };
  },

  applyData(data) {
    const localTime = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const newGames  = data.games || [];
    this.detectGameEvents(newGames, localTime);

    // Score bump: detect which score just changed before updating prevGames.
    // home_score / away_score covers the main score for team sports and
    // sets-won for tennis. For tennis we ALSO peek at score_text's last
    // set to detect games-within-set changes (much more frequent than
    // set wins) — used to bump the GAMES column on the Wimbledon card.
    const bumps = {};
    const _lastSetGames = (st) => {
      if (!st) return [0, 0];
      const sets = st.split(/[,\s]+/).filter(Boolean);
      const last = sets[sets.length - 1] || '0-0';
      const parts = last.split('-').map(n => parseInt(n, 10));
      return [parts[0] || 0, parts[1] || 0];
    };
    for (const g of newGames) {
      const prev = this.prevGames[g.game_id];
      if (!prev || g.status !== 2) continue;
      if ((g.away_score || 0) > (prev.away_score || 0)) bumps[g.game_id + '_away'] = true;
      if ((g.home_score || 0) > (prev.home_score || 0)) bumps[g.game_id + '_home'] = true;
      if (g.sport_id === 'tennis') {
        const [prevH, prevA] = _lastSetGames(prev.score_text);
        const [curH,  curA]  = _lastSetGames(g.score_text);
        if (curH > prevH) bumps[g.game_id + '_home_games'] = true;
        if (curA > prevA) bumps[g.game_id + '_away_games'] = true;
      }
    }
    if (Object.keys(bumps).length) {
      this.scoreBumpIds = bumps;
      setTimeout(() => { this.scoreBumpIds = {}; }, 1900);
    }

    // Merge instead of replace. Server occasionally returns a partial
    // game list (race during slate switches, brief team-name lookup drift
    // on tennis), and full replacement made games briefly disappear from
    // the Matches panel until the next poll. Merge keys by game_id —
    // existing games update, new games append, missing games linger
    // until selectSlate explicitly resets allGames.
    const incoming = new Map((newGames || []).map(g => [g.game_id, g]));
    const merged   = (this.allGames || []).map(g => incoming.get(g.game_id) || g);
    const seen     = new Set(merged.map(g => g.game_id));
    for (const g of (newGames || [])) {
      if (!seen.has(g.game_id)) merged.push(g);
    }
    this.allGames = merged;
    // The server-painted opening board stands down HERE, when there is a real
    // one to stand down for - not when Alpine booted, which is ~1.4s earlier
    // and the whole reason the frame exists (partials/shell_first_frame.html).
    if (merged.length) {
      try { document.documentElement.classList.add('ls-board-ready'); } catch (e) {}
      // ...and the same instant starts the visitor's reading window. The
      // headline engine holds its FIRST overlay until this + FIRST_GRACE, so
      // a stranger gets the board to themselves before the best thing the
      // product does lands on top of it (run 10, FIRST). Measured from here
      // rather than page load: a slow network must not spend their reading
      // time before there is anything to read.
      try { window.VidiHeadlineEngine && window.VidiHeadlineEngine.boardReady(); } catch (e) {}
    }
    for (const g of newGames) this.prevGames[g.game_id] = g;

    // LSSse.prunePins, NOT this.prunePins: these methods run via
    // .call(this, …) with `this` bound to the dashboard CONTROLLER, and the
    // controller only carries the three methods live.js delegates explicitly
    // (_sseUrl/_connectSSE/_applyData). A sibling in this file has to be
    // reached through the module. Calling it off `this` threw a TypeError on
    // every SSE tick, which killed the REST of applyData — the board showed
    // scoreboard scores from the initial fetch and every other widget stayed
    // empty (Xav 2026-08-17).
    LSSse.prunePins.call(this);

    if (data.n_lineups !== undefined) {
      this.nLineups  = data.n_lineups || 0;
      // Self-heal a mis-guessed spectator flag. n_lineups is USER-scoped (the
      // caller's own lineups on this slate). If it's positive while we're in
      // spectator mode, the cold-load guessed wrong (cache skew, or a signal
      // that arrived after the guess) — clear it and re-resolve the board so the
      // owner gets the real Simple/Analytical picker instead of the read-only
      // spectator board. Fires once (guard clears on first heal). (P0 audit.)
      if (this.nLineups > 0 && this.spectatorMode) {
        this.spectatorMode = false;
        if (this.selectedSlate) this.selectedSlate._spectator = false;
        if (this._resolveActiveForSport) this._resolveActiveForSport(this._sportId);
      }
      this.bestLive  = data.best_live || 0;
      this.avgLive   = data.avg_live  || 0;
      this.bestEst   = data.best_est  || 0;
      // Reactive flag — v2 host's kpiShouldRender + availableForSlot
      // gate projection-dependent widgets (% of pace, best est final)
      // on this. Defaults to false, flips true once any projection
      // row lands for this slate.
      this.hasProjections = !!data.has_user_projections;

      // Field-merge incoming over the cached players. The SSE sends full player
      // objects on the first push and DYNAMIC-ONLY deltas (id + live fields)
      // after; merging keeps the static bulk (name/photo/logo/team/pos/salary)
      // the delta omits, so every consumer below (event detection, prev-stats,
      // widgets) sees complete players. Dedupe guards: drop id-less entries;
      // update `seen` inside the push loop so a dup id is not pushed twice.
      const _mergeById = (cur, incoming) => {
        if (!incoming || !incoming.length) return cur || [];
        const incMap = new Map();
        for (const x of incoming) { const id = x && x.player_id; if (id) incMap.set(id, x); }
        const merged = (cur || []).map(x => {
          const inc = incMap.get(x.player_id);
          return inc ? { ...x, ...inc } : x;   // keep cached static, apply the delta
        });
        const seen = new Set(merged.map(x => x.player_id).filter(Boolean));
        for (const [id, x] of incMap) { if (!seen.has(id)) { merged.push(x); seen.add(id); } }
        return merged;
      };
      const myPlayers  = _mergeById(this.players,         data.players     || []);
      const allPlayers = _mergeById(this.allSlatePlayers, data.all_players || []);
      const myIds      = new Set(myPlayers.map(p => p.player_id));
      const evtPlayers = this.vidiScope === 'all' ? allPlayers : myPlayers;
      // Scorer feed: server-derived + server-scored (2026-07-05). The browser no
      // longer runs the scoring engine — it adapts data.scorer_events to the feed
      // and drips them. Personal-moment animations + prevStats snapshots below
      // are unaffected (they don't depend on client scoring).
      // First apply for this slate → seed the feed statically (no replay). Set
      // the flag regardless of event count so a pre-game slate that opens empty
      // still counts as seeded — its first LIVE event then drips normally.
      const _vidiSeedMode = !this._vidiSeeded;
      this._vidiSeeded = true;
      this._ingestScorerEvents(data.scorer_events || [], localTime, myIds, evtPlayers, _vidiSeedMode);

      const sport = (this.selectedSlate?.sport_id || '').toLowerCase();
      if (sport === 'soccer' && data.match_events?.length) {
        this._processMatchEvents(data.match_events, localTime, myIds);
      }
      // Play-by-play (broadcast tier) — MLB only today. Server ships the
      // full accumulated list per slate-live tick; we merge by id so the
      // _pbpFeed grows monotonically as new at-bats resolve. Newest at
      // FRONT so straight concat with `this.feed` in vidiEvents keeps
      // newest-first ordering. Cap at 60 (3× the scorer feed cap) so a
      // long extra-innings slate doesn't grow unbounded in memory.
      if (data.pbp_events?.length) {
        const known = new Set(this._pbpFeed.map(e => e.id));
        const fresh = [];
        for (const ev of data.pbp_events) {
          if (!known.has(ev.id)) fresh.push(ev);
        }
        if (fresh.length) {
          // Newest first inside `fresh` (server ships ascending by
          // at_bat_index, so reverse before prepending).
          this._pbpFeed = [...fresh.reverse(), ...this._pbpFeed].slice(0, 60);
        }
      }

      // Per-player score flash (tiered by delta) + row delta overlay
      //
      // SEEDED on the first apply, for the same reason the Wire and the moment
      // layer are (_seedVidiFeed, _pmSeeded). There is no previous frame to
      // diff against when the board opens: `this.players` is still empty, so
      // every already-scoring player reads as having gained his whole night in
      // one tick. Measured on a mid-slate open: 7 players flashed and the row
      // overlays read +16 / +14 / +14 / +3.4 / +3 / +2 / +1.4 against totals of
      // exactly 16 / 14 / 14 / 3.4 / 3 / 2 / 1.4 - the board pretending the
      // whole night just happened, at the moment somebody arrives to catch up.
      // The numbers were right; the animation was lying (run 10, LIVE-23).
      const seeding = !this._flashSeeded;
      this._flashSeeded = true;
      const prevPts  = {};
      for (const p of this.players) prevPts[p.player_id] = p.live_fpts || 0;
      let toFlash  = {};
      let toDeltas = {};
      for (const p of myPlayers) {
        if (p.game_phase === 'not_started') continue;
        const delta = Math.round(((p.live_fpts || 0) - (prevPts[p.player_id] || 0)) * 100) / 100;
        if (delta <= 0) continue;
        toFlash[p.player_id]  = delta >= 8 ? 'high' : delta >= 3 ? 'mid' : 'low';
        toDeltas[p.player_id] = delta;
      }
      // Nothing happened while you were not here. The next apply has a real
      // previous frame and behaves normally.
      if (seeding) { toFlash = {}; toDeltas = {}; }

      // Personal moments — derived from live state diffs, no platform
      // connector required. Fires the same headline-engine that in-game
      // events use (HOME RUN / GOAL / etc.), so users get one unified
      // animation system + sound + style picker for both kinds.
      this._detectPersonalMoments(toDeltas, myPlayers);

      if (Object.keys(toFlash).length) {
        this.flashIds    = { ...this.flashIds,    ...toFlash  };
        this.fptDeltaMap = { ...this.fptDeltaMap, ...toDeltas };
        const pids = Object.keys(toFlash);
        setTimeout(() => {
          const d = { ...this.fptDeltaMap };
          for (const pid of pids) delete d[pid];
          this.fptDeltaMap = d;
        }, 1900);
        setTimeout(() => {
          const f = { ...this.flashIds };
          for (const pid of pids) delete f[pid];
          this.flashIds = f;
        }, 4000);
      }

      // Stat cell flash: detect which stats changed for my players
      for (const p of myPlayers) {
        const prev = this.prevStats[p.player_id];
        if (!prev || p.game_phase === 'not_started') continue;
        for (const [k, v] of Object.entries(p.stats || {})) {
          if ((v || 0) !== (prev[k] || 0)) this._flashStat(p.player_id, k);
        }
      }

      // prevStats: my players only (live_fpts may be scaled by CPT/VC multiplier).
      // Drop entries for players missing from this snapshot — a player who
      // briefly vanishes (server partial response, api hiccup) and then
      // reappears would otherwise diff against their *original* stats from
      // when they first loaded, firing a burst of backfilled events
      // ("Game Lost (1) … (7)") on the rebound. Dropping their prev costs
      // one extra "no-prev → skip" tick when they return — small price for
      // never backfilling.
      // Only write a real prev when the player has actual stat keys —
      // an empty {} stats blob would write a shell prev that the engine
      // would later diff a full cumulative against, firing a burst.
      // The previous prev (if any) is preserved when current stats are
      // empty; if the player isn't in this snapshot at all, drop them.
      const _hasStatKeys = (s) => s && Object.keys(s).length > 0;
      const myIdSet = new Set(myPlayers.map(p => p.player_id));
      for (const pid of Object.keys(this.prevStats)) {
        if (!myIdSet.has(pid)) delete this.prevStats[pid];
      }
      for (const p of myPlayers) {
        if (!_hasStatKeys(p.stats)) continue;
        this.prevStats[p.player_id] = {
          ...p.stats,
          live_fpts: p.live_fpts || 0,
          oncourt:   p.oncourt,
        };
      }
      // prevAllStats: all slate players at base (1x) scale — used by 'all' scope
      const allIdSet = new Set(allPlayers.map(p => p.player_id));
      for (const pid of Object.keys(this.prevAllStats)) {
        if (!allIdSet.has(pid)) delete this.prevAllStats[pid];
      }
      for (const p of allPlayers) {
        if (!_hasStatKeys(p.stats)) continue;
        this.prevAllStats[p.player_id] = {
          ...p.stats,
          live_fpts: p.live_fpts || 0,
          oncourt:   p.oncourt,
        };
      }
      // Players were field-merged into `myPlayers` / `allPlayers` at the top of
      // this block (full on first push, dynamic-only deltas after), so just
      // commit them — a partial response never drops a player or its static data.
      //
      // Freeze each player (+ its nested stats) before committing. Alpine's
      // reactivity engine (@vue/reactivity) SKIPS frozen objects, so the 500+
      // player pool is no longer deep-proxied — that proxy/effect churn was the
      // #1 remaining cost in the load profile (~5s of pure Alpine internals on a
      // big slate). Safe here: players are strictly immutable — the merge above
      // produces new objects via spread and nothing mutates a player/stat in
      // place (audited). Idempotent + cheap: unchanged players are already frozen
      // (the isFrozen guard skips them), and array replacement still triggers the
      // re-render, so live updates are unaffected.
      const _freeze = (p) => {
        if (p && typeof p === 'object' && !Object.isFrozen(p)) {
          if (p.stats && typeof p.stats === 'object' && !Object.isFrozen(p.stats)) Object.freeze(p.stats);
          Object.freeze(p);
        }
        return p;
      };
      myPlayers.forEach(_freeze);
      allPlayers.forEach(_freeze);
      this.players         = myPlayers;
      // Lineup rows: a delta push (core/slate_live.lean_lineups_delta) sends
      // each lineup player's CHANGED fields only - merge onto the row we hold,
      // keyed (lineup, player, slot). An unknown player arrived in full.
      let _rows = data.lineups || [];
      if (data._lineups_delta && (this.lineupRows || []).length) {
        const _k = (rid, p) => rid + '|' + p.player_id + '|' + p.slot;
        const _held = new Map();
        for (const r of this.lineupRows) for (const p of (r.players || [])) _held.set(_k(r.id, p), p);
        _rows = _rows.map(r => ({ ...r, players: (r.players || []).map(p => {
          const o = _held.get(_k(r.id, p));
          return o ? { ...o, ...p } : p;
        }) }));
      }
      // Frozen like the players above, so Alpine doesn't deep-proxy 150 x 9
      // lineup players on every push (Phase 10). Rows stay plain objects.
      for (const r of _rows) (r.players || []).forEach(_freeze);
      this.lineupRows      = _rows;
      this.allSlatePlayers = allPlayers;
      this.dreamTeam       = data.dream_team || [];
      this.salaryCap       = data.salary_cap || 0;
      // The phone hero's pulse, once the rows it reads are THIS frame's. It
      // used to run above, against the previous frame's lineupRows: every
      // pulse landed one update after its number moved, and the first frame
      // after load pulsed as if the board had jumped (run 8, LIVE-13).
      // No-op on desktop.
      if (typeof this._shellHeroTick === 'function') this._shellHeroTick();
    }

    // Slate scoring format ('single_stat_hr' etc.; '' for standard). Slate-level,
    // so accept it off any push that carries it — drives the vidi money-event
    // filter (single-stat feeds lock to the format's one scoring event).
    if ('format_variant' in data) this.formatVariant = data.format_variant || '';

    // D/ST points-allowed tiers (NFL/CFB) for the D/ST Bracket panel. The
    // server has sent these all along, but nothing copied them onto the
    // board, so the panel read "+null" for every defense (2026-10-05).
    if ('pa_brackets' in data) this.pa_brackets = data.pa_brackets || [];

    // Server-reconstructed trajectory (real, from slate start; survives F5 /
    // slate-hop). Only assign when present so a lean delta push doesn't wipe it.
    if ('lineupHistory' in data) this.lineupHistory = data.lineupHistory || [];

    if (data.last_updated)    this.lastUpdated    = data.last_updated;
    if ('data_updated_at' in data) this.dataUpdatedAt = data.data_updated_at;
    this.dataReady = true;
    if (this._awaitFirstPush) {
      // First data after a slate (re)select — let the widgets mount + render
      // under the transition overlay, then drop it in one frame-settled go so
      // the dashboard appears fully-formed instead of widget-by-widget.
      this._awaitFirstPush = false;
      clearTimeout(this._switchFallback);
      this.$nextTick(() => requestAnimationFrame(() => requestAnimationFrame(() =>
        setTimeout(() => { this._switching = false; }, 90))));
    }
    // The demo used to hold the load screen until the replay had ACTION, so a
    // 0-0 kickoff would not read as "stalled". Measured, that cost 13.6s of
    // full-bleed beacon - and it lands on the single highest-friction moment
    // this product has: a stranger arriving from a Reddit link on a phone. A
    // board at 0-0 with the teams, the clock and a full lineup is not an empty
    // screen, and it is the honest one. Reveal as soon as there is data
    // (Xav 2026-09-12, reversing the earlier hold).
    this._demoRevealed = true;
    this.loading = false;
    this._flashSync();
  },

  /* Auto-unpin games that have FINISHED, so a dead pin stops eating a PIN_CAP
   * slot that a live game wants.
   *
   * TWO THINGS IT MUST NOT DO.
   *
   * 1  Prune a USER pin. Games pinned by hand via togglePin are tracked in
   *    _userPinnedGames and survive every status change — if you pinned a
   *    finished game from Matches, you are looking at it on purpose.
   *
   * 2  Prune when there is nothing to make room FOR. If every game on the
   *    slate has finished, pruning has no upside and exactly one downside: it
   *    empties the scoreboard. That is the ENDED-slate case — Lineups → Ended →
   *    open a final board — where it ran as a loop with no exit: the auto-pin
   *    filled the board, the next tick stripped every pin (all finished, none
   *    user-pinned), _savePinnedGames persisted the EMPTY set, and the reload
   *    auto-pinned again so it could be stripped again. The board was never
   *    supposed to be empty; it was emptied, every time, immediately.
   *    (Xav 2026-08-17: "games should always be pinned by default in every
   *    slate/every scenario".)
   *
   * Extracted from applyData so it can be unit-tested — the loop above is
   * indistinguishable from correct until you look at a slate where every game
   * is over.
   */
  prunePins() {
    if (!(this.pinnedGames && this.pinnedGames.size)) return false;
    const games = this.allGames || [];
    // Rule 2: something must still be running (or yet to start) for a freed
    // slot to be worth anything.
    if (!games.some(g => this.effStatus(g) < 3)) return false;

    const finishedIds = new Set(
      games.filter(g => this.effStatus(g) >= 3).map(g => g.game_id)
    );
    let pruned = false;
    const next = new Set();
    for (const gid of this.pinnedGames) {
      const isFinished = finishedIds.has(gid);
      const isUserPin  = this._userPinnedGames && this._userPinnedGames.has(gid);
      if (isFinished && !isUserPin) pruned = true;
      else next.add(gid);
    }
    if (pruned) {
      this.pinnedGames = next;
      this._savePinnedGames();
    }
    return pruned;
  },
};
